/**
 * Shared layout and styled primitives for the Marketing "Create SMS Blast" page.
 * Used by NewMessagePage and EventRegistrantSmsComposer (standalone) for visual parity.
 */
import styled from 'styled-components';
import { Link } from 'react-router-dom';

export const PageWrap = styled.div`
  min-height: 100%;
  background: var(--background);
  padding: 1.5rem;
`;

export const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

export const BackLink = styled(Link)`
  color: var(--onSurfaceVariant);
  text-decoration: none;
  font-size: 0.9rem;
  &:hover {
    color: var(--primary);
  }
`;

export const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 1.5rem;
  align-items: start;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

export const Main = styled.main`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const PageTitle = styled.h1`
  margin: 0 0 0.25rem 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--onSurface);
`;

export const PageSub = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: var(--onSurfaceVariant);
`;

export const Card = styled.section`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

export const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const StepNum = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--primary);
  color: var(--onPrimary);
  font-weight: 700;
  font-size: 0.9rem;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--onSurface);
`;

export const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`;

export const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--onSurfaceVariant);
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

export const TextInput = styled.input`
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  font-size: 0.95rem;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  &::placeholder {
    color: var(--onSurfaceVariant);
  }
  &:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--surface);
  }
  &:disabled {
    opacity: 0.85;
    cursor: not-allowed;
  }
`;

export const Select = styled.select`
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  font-size: 0.95rem;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23B0BEC5' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  padding-right: 2rem;
  &:focus {
    outline: none;
    border-color: var(--primary);
    background-color: var(--surface);
  }
`;

export const TagChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

export const TagChip = styled.button`
  padding: 0.35rem 0.6rem;
  border: 1px solid var(--outline);
  border-radius: 6px;
  background: var(--surfaceVariant);
  color: var(--primary);
  font-size: 0.85rem;
  font-family: monospace;
  cursor: pointer;
  &:hover {
    background: var(--surface);
    border-color: var(--primary);
  }
`;

export const MessageTextarea = styled.textarea`
  width: 100%;
  min-height: 140px;
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.95rem;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  resize: vertical;
  &::placeholder {
    color: var(--onSurfaceVariant);
  }
  &:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--surface);
  }
`;

export const EncodingRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
`;

export const CharCount = styled.span`
  color: var(--onSurfaceVariant);
  .purple {
    color: var(--primary);
    font-weight: 600;
  }
`;

export const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const RadioOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  font-size: 0.95rem;
  color: var(--onSurface);
`;

export const RadioInput = styled.input`
  margin-top: 0.25rem;
  accent-color: var(--primary);
  width: 1.1rem;
  height: 1.1rem;
  cursor: pointer;
`;

export const RadioDesc = styled.span`
  display: block;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
  margin-top: 0.15rem;
`;

export const DateTimeRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1rem;
  margin-top: 1rem;
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

export const InputWithIcon = styled.div`
  position: relative;
  min-width: 0;
  input {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    border: 1px solid var(--outline);
    border-radius: 8px;
    padding: 0.6rem 0.75rem 0.6rem 2.5rem;
    font-size: 0.95rem;
    background: var(--surfaceVariant);
    color: var(--onSurface);
    &:focus {
      outline: none;
      border-color: var(--primary);
      background: var(--surface);
    }
  }
  svg {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--onSurfaceVariant);
    pointer-events: none;
  }
`;

export const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  position: sticky;
  top: 1rem;
`;

export const SidebarCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

export const SidebarTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--onSurfaceVariant);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const PhoneMock = styled.div`
  border: 2px solid var(--outline);
  border-radius: 24px;
  padding: 12px;
  background: var(--surface);
  max-width: 280px;
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

export const PhoneScreen = styled.div`
  background: var(--surfaceVariant);
  border-radius: 16px;
  padding: 1rem;
  min-height: 320px;
  display: flex;
  flex-direction: column;
`;

export const PhoneHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--onSurface);
`;

export const PhoneBack = styled.span`
  color: var(--onSurfaceVariant);
  cursor: default;
`;

export const PhoneTime = styled.div`
  font-size: 0.7rem;
  color: var(--onSurfaceVariant);
  margin-bottom: 0.5rem;
`;

export const PhoneBubble = styled.div`
  background: var(--primary);
  color: var(--onPrimary);
  padding: 0.6rem 0.85rem;
  border-radius: 12px 12px 12px 4px;
  font-size: 0.85rem;
  line-height: 1.4;
  max-width: 90%;
  align-self: flex-start;
  white-space: pre-wrap;
`;

export const PhoneInputLabel = styled.div`
  margin-top: auto;
  font-size: 0.7rem;
  color: var(--onSurfaceVariant);
  padding: 0.5rem 0;
`;

export const BtnRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
